import { z } from 'zod';

/**
 * Cold-outreach marketing — pure helpers shared by the API + tooling: lead-row
 * parsing from the ICP CSV, the hard daily-send cap, and campaign input validation.
 * No I/O here.
 */

/** DEFAULT per-day ceiling when the account hasn't configured its own max. */
export const MAX_DAILY_CAP = 15;
export const DEFAULT_DAILY_CAP = 12;
/** Absolute safety ceiling — the configurable account max can never exceed this. */
export const ABSOLUTE_MAX_DAILY_CAP = 1000;

/** Resolve the effective per-day ceiling: the configured `max` (default 15),
 * floored to ≥1 and capped at the absolute safety ceiling. */
export function effectiveDailyMax(max?: number | null): number {
  const m = Math.floor(Number(max ?? MAX_DAILY_CAP));
  if (!Number.isFinite(m)) return MAX_DAILY_CAP;
  return Math.max(1, Math.min(ABSOLUTE_MAX_DAILY_CAP, m));
}

/** Clamp a configured daily cap into 1..max. `max` defaults to MAX_DAILY_CAP (15);
 * pass the account's configured ceiling to allow a higher cap. */
export function clampDailyCap(n: number | null | undefined, max?: number | null): number {
  const ceil = effectiveDailyMax(max);
  const v = Math.floor(Number(n ?? DEFAULT_DAILY_CAP));
  if (!Number.isFinite(v)) return Math.min(DEFAULT_DAILY_CAP, ceil);
  return Math.max(1, Math.min(ceil, v));
}

/** How many more outbound emails a campaign may send today. Never negative. */
export function remainingDailyQuota(
  sentToday: number,
  dailyCap: number | null | undefined,
  max?: number | null,
): number {
  return Math.max(0, clampDailyCap(dailyCap, max) - Math.max(0, Math.floor(sentToday)));
}

export interface LeadInput {
  id: string;
  tier: number;
  icpScore: number | null;
  segment: string | null;
  emailMatchesAgency: boolean;
  siteLive: boolean;
  insuranceContent: boolean;
  hasContactForm: boolean;
  websiteCheckedUrl: string | null;
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  employees: number | null;
  companyWebsite: string | null;
  domain: string | null;
}

const str = (v: string | undefined): string | null => {
  const s = (v ?? '').trim();
  return s === '' ? null : s;
};
const bool = (v: string | undefined): boolean => (v ?? '').trim() === '1';
const int = (v: string | undefined): number | null => {
  const s = (v ?? '').trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.trunc(n) : null;
};

/**
 * Map one parsed CSV record (header→value) to a LeadInput. Returns null for rows
 * with no id or no email (unusable for outreach). Tier defaults to 9 (worst) when
 * absent so unscored rows sort last.
 */
export function leadFromCsvRecord(rec: Record<string, string | undefined>): LeadInput | null {
  const id = str(rec.id);
  const email = str(rec.email_address)?.toLowerCase() ?? null;
  if (!id || !email) return null;
  return {
    id,
    tier: int(rec.tier) ?? 9,
    icpScore: int(rec.icp_score),
    segment: str(rec.segment)?.toLowerCase() ?? null,
    emailMatchesAgency: bool(rec.email_matches_agency),
    siteLive: bool(rec.site_live),
    insuranceContent: bool(rec.insurance_content),
    hasContactForm: bool(rec.has_contact_form),
    websiteCheckedUrl: str(rec.website_checked_url),
    companyName: str(rec.company_name),
    firstName: str(rec.first_name),
    lastName: str(rec.last_name),
    title: str(rec.title),
    email,
    phone: str(rec.phone_number),
    city: str(rec.city),
    state: str(rec.state),
    zip: str(rec.zip),
    employees: int(rec.employees),
    companyWebsite: str(rec.company_website),
    domain: str(rec.domain)?.toLowerCase() ?? null,
  };
}

/** Campaign create/update input (API boundary). */
export const campaignInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  segment: z.string().trim().max(40).nullish(),
  maxTier: z.number().int().min(0).max(9).nullish(),
  // Which lead-status bucket to enroll from (mirrors the Leads browser). Defaults
  // to 'enrollable' (verified-deliverable, not yet sent) when unset.
  leadStatus: z
    .enum([
      'enrollable',
      'sent',
      'bounced',
      'invalid',
      'risky',
      'unsubscribed',
      'ineligible',
      'suppressed',
      'all',
    ])
    .nullish(),
  // Validate against the absolute ceiling; the route clamps to the account's
  // configured max (default 15) on save.
  dailyCap: z.number().int().min(1).max(ABSOLUTE_MAX_DAILY_CAP).optional(),
  fromName: z.string().trim().max(120).optional(),
  fromEmail: z.string().trim().email().optional(),
  replyTo: z.string().trim().email().nullish(),
  bcc: z.string().trim().email().nullish(),
  brief: z.string().trim().max(4000).nullish(),
});
export type CampaignInput = z.infer<typeof campaignInputSchema>;
